/**
 * Iter 187 — useShareable hook + <ShareMenu> popover.
 *
 * Provides:
 *   - Long-press (touch hold ≥ 500ms) on mobile
 *   - Right-click (contextmenu) on desktop
 *   → opens a tiny inline menu anchored at the touch/click point with
 *     two actions: "Share…" (calls navigator.share with the file) and
 *     "Download" (saves the file to the device).
 *
 * NO MODAL — the menu is a small absolutely-positioned popover that
 * dismisses on outside-tap. Stays within the Trust Contract.
 *
 * Usage:
 *   const { handlers, menu } = useShareable({
 *     url: photoUrl,
 *     filename: "JC-0123-photo-3.jpg",
 *     contentType: "image/jpeg",
 *     title: "JC-0123 progress photo",
 *   });
 *   return (
 *     <>
 *       <img {...handlers} src={photoUrl} />
 *       {menu}
 *     </>
 *   );
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Share2, Download } from "lucide-react";
import { shareFile, resolveShareDescriptor } from "../lib/shareFile";
import { toast } from "sonner";

const LONG_PRESS_MS = 500;
const MOVE_TOLERANCE_PX = 8;

const _downloadDirect = async (descriptor) => {
  // Re-uses shareFile's download fallback by forcing a non-share platform.
  // Simpler: fetch + save (mirrors shareFile internals).
  try {
    const resp = await fetch(descriptor.url, { credentials: "omit" });
    if (!resp.ok) throw new Error("HTTP " + resp.status);
    const blob = await resp.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objUrl;
    a.download = descriptor.filename || "download";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(objUrl), 30_000);
    toast.success("File downloaded");
  } catch (err) {
    toast.error("Download failed", { description: err?.message?.slice(0, 120) });
  }
};

export function useShareable(fileInput, options = {}) {
  const { disabled = false } = options;
  const [menuPos, setMenuPos] = useState(null); // { x, y } or null
  const longPressTimer = useRef(null);
  const startPos = useRef(null);
  const triggeredFromTouch = useRef(false);

  const open = useCallback((x, y) => {
    setMenuPos({ x, y });
  }, []);

  const close = useCallback(() => setMenuPos(null), []);

  // Dismiss on outside tap / scroll
  useEffect(() => {
    if (!menuPos) return undefined;
    const onTap = (e) => {
      // Only close on taps OUTSIDE the menu node
      const node = document.getElementById("hv-share-menu");
      if (node && node.contains(e.target)) return;
      close();
    };
    const onScroll = () => close();
    // Use capture so we catch the tap before bubbled handlers.
    setTimeout(() => {
      document.addEventListener("pointerdown", onTap, true);
      document.addEventListener("scroll", onScroll, true);
    }, 0);
    return () => {
      document.removeEventListener("pointerdown", onTap, true);
      document.removeEventListener("scroll", onScroll, true);
    };
  }, [menuPos, close]);

  const onContextMenu = useCallback((e) => {
    if (disabled) return;
    if (!resolveShareDescriptor(fileInput)) return;
    e.preventDefault();
    e.stopPropagation();
    triggeredFromTouch.current = false;
    open(e.clientX, e.clientY);
  }, [disabled, fileInput, open]);

  const cancelLongPress = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const onTouchStart = useCallback((e) => {
    if (disabled) return;
    if (!resolveShareDescriptor(fileInput)) return;
    const t = e.touches?.[0];
    if (!t) return;
    startPos.current = { x: t.clientX, y: t.clientY };
    cancelLongPress();
    longPressTimer.current = setTimeout(() => {
      // Haptic if available
      try { navigator.vibrate?.(15); } catch { /* ignore */ }
      triggeredFromTouch.current = true;
      open(startPos.current.x, startPos.current.y);
      // Prevent the regular tap/click that's queued after touchend
    }, LONG_PRESS_MS);
  }, [disabled, fileInput, cancelLongPress, open]);

  const onTouchMove = useCallback((e) => {
    if (!longPressTimer.current || !startPos.current) return;
    const t = e.touches?.[0];
    if (!t) return;
    const dx = Math.abs(t.clientX - startPos.current.x);
    const dy = Math.abs(t.clientY - startPos.current.y);
    if (dx > MOVE_TOLERANCE_PX || dy > MOVE_TOLERANCE_PX) cancelLongPress();
  }, [cancelLongPress]);

  const onTouchEnd = useCallback(() => {
    cancelLongPress();
  }, [cancelLongPress]);

  const onClick = useCallback((e) => {
    // Suppress the click that follows a long-press release.
    if (triggeredFromTouch.current) {
      e.preventDefault();
      e.stopPropagation();
      triggeredFromTouch.current = false;
    }
  }, []);

  const handlers = {
    onContextMenu,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    onTouchCancel: onTouchEnd,
    onClickCapture: onClick,
  };

  const desc = resolveShareDescriptor(fileInput);
  const menu = menuPos ? (
    <div
      id="hv-share-menu"
      role="menu"
      data-testid="share-menu"
      className="fixed z-[300] min-w-[180px] rounded-xl border border-white/10 bg-[#0e1620]/95 backdrop-blur-md shadow-2xl py-1 text-sm text-white"
      style={{
        left: Math.min(menuPos.x, window.innerWidth - 200),
        top: Math.min(menuPos.y, window.innerHeight - 120),
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-white/5 active:bg-white/10"
        data-testid="share-menu-share"
        onClick={async () => {
          close();
          await shareFile(fileInput);
        }}
      >
        <Share2 className="w-4 h-4 text-[#1FB6C1]" />
        <span>Share file…</span>
      </button>
      <button
        type="button"
        className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-white/5 active:bg-white/10"
        data-testid="share-menu-download"
        onClick={async () => {
          close();
          if (desc) await _downloadDirect(desc);
        }}
      >
        <Download className="w-4 h-4 text-emerald-300" />
        <span>Save to device</span>
      </button>
    </div>
  ) : null;

  return { handlers, menu, shareNow: () => shareFile(fileInput), isOpen: !!menuPos };
}
